import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { useLocale } from '@/lib/i18n/useLocale';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { useGate } from '@/lib/rbac/Gate';
import { resolveI18n } from '@/lib/i18n/content';
import { ENTITY_TYPE_TO_COLLECTION, getCollection } from '@/config/collections';
import type { I18n } from '@/lib/api/types';
import { PAGE_COPY_BASE } from '@/features/pageCopy/routes';
import { editorPath, locateField } from '@/features/pageEditor/outline';
import { useOutlines } from '@/features/pageEditor/useOutlines';
import type { PageOutline } from '@/lib/api/types';

export interface SearchHit {
  entity_type: string;
  /** Canonical id returned by the search API (UUID). */
  entity_id?: string;
  id?: string;
  slug?: string;
  key?: string;
  title: I18n | string;
  // Page-copy hits (entity_type `page_copy`) carry the namespace + matched field so we can
  // deep-link to exactly that field, plus a short snippet for context.
  namespace?: string;
  field_path?: string;
  snippet?: string;
}

/** Top-bar global search over content + site copy (spec §13). Deep-links to the editor. */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useLocale();
  // Page copy opens with `content:read` (Content → Page copy). Hide those hits from users who
  // can't open it (defense-in-depth; the API gates them too).
  const canReadPageCopy = useGate('content:read').allowed;
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(q, 250);
  const ref = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: keys.search(debounced, locale),
    queryFn: () => api.get<SearchHit[]>('/search', { q: debounced, locale }),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const hits = useMemo(
    () =>
      (query.data ?? [])
        .filter((h) => h.entity_type !== 'page_copy' || canReadPageCopy)
        .slice(0, 8),
    [query.data, canReadPageCopy],
  );

  // Page-text hits open the page that shows the text, on its section; the outlines are only
  // fetched once such a hit appears.
  const outlines = useOutlines(hits.some((h) => h.entity_type === 'page_copy'));

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function go(hit: SearchHit) {
    // Carry the searched term so the content editor can scroll to + highlight the matched field
    // (page-copy hits already deep-link by field_path and ignore `q`).
    const target = routeForHit(hit, q, outlines.data?.outlines);
    if (target) navigate(target);
    setOpen(false);
    setQ('');
  }

  return (
    <div ref={ref} className="relative max-w-md">
      <input
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('search.placeholder')}
        aria-label={t('search.ariaLabel')}
        className="w-full rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-sm py-[8px] text-small focus:border-accent-blue focus-visible:shadow-focus focus:outline-none"
      />
      {open && debounced.trim().length >= 2 && (
        <div className="absolute z-50 mt-xs w-full overflow-hidden rounded-md border-[0.5px] border-hairline bg-canvas-paper shadow-nav-scrolled">
          {/* One polite region for the result count states, so a screen reader hears
              "Searching…", "No results" or the failure without the list being read. */}
          <div role="status" aria-live="polite">
            {query.isLoading && (
              <p className="p-sm text-small text-ink-mute">{t('search.searching')}</p>
            )}
            {query.isError && (
              <p className="p-sm text-small text-signal-error">{t('states.search.errorTitle')}</p>
            )}
            {query.data && hits.length === 0 && (
              <p className="p-sm text-small text-ink-mute">{t('search.noResults')}</p>
            )}
          </div>
          {hits.map((hit, i) => (
            <button
              type="button"
              key={i}
              onClick={() => go(hit)}
              className="flex min-h-[44px] w-full items-start justify-between gap-sm px-sm py-xs text-start transition-colors duration-almanac hover:bg-canvas-sand focus-visible:shadow-focus"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-small text-ink-graphite">
                  {hitPrimary(hit, locale)}
                </span>
                {hitSecondary(hit) && (
                  <span className="truncate font-mono text-label-sm text-ink-mute">
                    {hitSecondary(hit)}
                  </span>
                )}
              </span>
              <span className="shrink-0 font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">
                {t(`search.entity.${hit.entity_type}`, { defaultValue: hit.entity_type })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The main line for a hit: the highlighted snippet (page copy) or the resolved title. */
function hitPrimary(hit: SearchHit, locale: 'en' | 'ar'): ReactNode {
  if (hit.snippet) return renderSnippet(hit.snippet);
  return typeof hit.title === 'string' ? hit.title : resolveI18n(hit.title, locale);
}

/**
 * Render a search snippet into React nodes, turning the server's `<em>…</em>` match markup
 * into styled highlights. The rest of the snippet is HTML-escaped by the API (`&lt;`, `&amp;`,
 * …); we decode those entities and render everything as plain text — never `innerHTML` — so a
 * crafted copy value can't inject markup.
 */
function renderSnippet(snippet: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /<em>([\s\S]*?)<\/em>/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(snippet)) !== null) {
    if (m.index > last) parts.push(decodeEntities(snippet.slice(last, m.index)));
    parts.push(
      <mark
        key={key++}
        className="rounded-[2px] bg-accent-blue/15 px-[2px] font-medium text-ink-deep"
      >
        {decodeEntities(m[1])}
      </mark>,
    );
    last = m.index + m[0].length;
  }
  if (last < snippet.length) parts.push(decodeEntities(snippet.slice(last)));
  return parts;
}

/** Decode the HTML entities the search API emits (Go's html.EscapeString set). `&amp;` last. */
export function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;|&#34;/g, '"')
    .replace(/&#39;|&#x27;/gi, "'")
    .replace(/&amp;/g, '&');
}

/** A muted second line giving location context (page-copy namespace · field). */
function hitSecondary(hit: SearchHit): string | null {
  if (hit.entity_type !== 'page_copy') return null;
  const ns = hit.namespace ?? hit.key ?? '';
  return hit.field_path ? `${ns} · ${hit.field_path}` : ns || null;
}

/** Arabic Unicode block — used to tell which locale a snippet's matched text is in. */
const ARABIC_RE = /[؀-ۿ]/;

/**
 * Which locale the hit actually matched in. Page copy is bilingual and the search index spans
 * both locales regardless of the UI's `locale` param, so a hit's *matched* copy can be Arabic
 * even when the console is in English. We infer it from the highlighted (`<em>…`) fragment of
 * the snippet — that's the text that matched — so the editor can land on the right-language
 * field instead of its translation twin. Returns null when there's no snippet to judge by.
 */
export function matchedLocale(hit: SearchHit): 'en' | 'ar' | null {
  if (!hit.snippet) return null;
  const ems = hit.snippet.match(/<em>([\s\S]*?)<\/em>/g);
  const matched = ems ? ems.join(' ') : hit.snippet;
  return ARABIC_RE.test(matched) ? 'ar' : 'en';
}

/**
 * Map a search hit to its admin editor route (by entity type). Exported for tests.
 *
 * `q` is the searched term: content hits carry no field-level data from the API, so we pass it
 * through as `?q=` and let the editor scroll to + highlight the field that matched. Page-copy
 * hits deep-link by their own `field_path`/`loc` and ignore `q`.
 */
export function routeForHit(hit: SearchHit, q?: string, outlines?: PageOutline[]): string | null {
  if (hit.entity_type === 'page_copy') {
    const ns = hit.namespace ?? hit.key ?? hit.entity_id;
    if (!ns) return null;
    // The page that renders this text, opened on its section and field, in the language the
    // words matched in (the editor shows one language at a time).
    const found = outlines ? locateField(outlines, ns, hit.field_path) : null;
    if (found) {
      const field = (hit.field_path ?? '').replace(/^(en|ar)\./, '');
      const lang = /^(en|ar)\./.exec(hit.field_path ?? '')?.[1] ?? matchedLocale(hit);
      let at = editorPath(found.outline, found.region.id);
      if (field) at += `&field=${encodeURIComponent(field)}`;
      return lang ? `${at}&lang=${lang}` : at;
    }
    const base = `${PAGE_COPY_BASE}/${encodeURIComponent(ns)}`;
    if (!hit.field_path) return base;
    let url = `${base}?field=${encodeURIComponent(hit.field_path)}`;
    // Land on the copy the user actually matched (e.g. the Arabic twin), not the UI-locale one.
    const loc = matchedLocale(hit);
    if (loc) url += `&loc=${loc}`;
    return url;
  }

  // The search API returns the canonical UUID as `entity_id`; editors load by UUID. Fall
  // back to id/slug/key only if entity_id is absent.
  const id = hit.entity_id ?? hit.id ?? hit.slug ?? hit.key;
  if (!id) return null;
  let base: string | null;
  switch (hit.entity_type) {
    case 'page':
      base = `/pages/${id}`;
      break;
    case 'family':
    case 'product_family':
      base = `/families/${id}`;
      break;
    default: {
      // Resolve the real collection segment via the registry (never naive "+s"
      // pluralization, which breaks industry→industries and job→job-postings).
      const collection = ENTITY_TYPE_TO_COLLECTION[hit.entity_type] ?? hit.entity_type;
      base = getCollection(collection) ? `/content/${collection}/${id}` : null;
    }
  }
  if (!base) return null;
  const term = q?.trim();
  return term ? `${base}?q=${encodeURIComponent(term)}` : base;
}
